import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { MessageSquare, Send, Trash2, Bot, User } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const SUGGESTIONS = [
  "Jelaskan Dual Process Theory dan relevansinya untuk konten kreator",
  "Bagaimana cara kerja dark patterns di Instagram?",
  "Apa bedanya nudge dan manipulasi dalam UX?",
  "Bagaimana AI Netflix merekomendasikan konten?",
];

export default function AiChat() {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const [currentResponse, setCurrentResponse] = useState("");
  const [loading, setLoading] = useState(true);
  const bottomRef = useRef(null);

  useEffect(() => {
    axios.get(`${API}/chat/history`)
      .then(r => setMessages(r.data))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, currentResponse]);

  const sendMessage = async (text) => {
    const msg = (text || input).trim();
    if (!msg || streaming) return;
    setInput("");

    const userMsg = { role: "user", content: msg, created_at: new Date().toISOString(), id: Date.now() };
    setMessages(prev => [...prev, userMsg]);
    setStreaming(true);
    setCurrentResponse("");

    try {
      const res = await fetch(`${BACKEND_URL}/api/chat/send`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ message: msg })
      });

      if (!res.ok) throw new Error("Network error");
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let full = "";

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        const chunk = decoder.decode(value, { stream: true });
        const lines = chunk.split("\n").filter(l => l.startsWith("data: "));
        for (const line of lines) {
          try {
            const data = JSON.parse(line.slice(6));
            if (data.type === "delta") {
              full += data.content;
              setCurrentResponse(full);
            } else if (data.type === "done" || data.type === "error") {
              break;
            }
          } catch (_) {}
        }
      }

      const assistantMsg = { role: "assistant", content: full, created_at: new Date().toISOString(), id: Date.now() + 1 };
      setMessages(prev => [...prev, assistantMsg]);
    } catch (err) {
      setMessages(prev => [...prev, {
        role: "assistant", content: "Terjadi kesalahan. Coba kirim ulang.", created_at: new Date().toISOString(), id: Date.now() + 1
      }]);
    } finally {
      setStreaming(false);
      setCurrentResponse("");
    }
  };

  const clearChat = async () => {
    await axios.delete(`${API}/chat/history`);
    setMessages([]);
  };

  return (
    <div className="flex flex-col h-[calc(100vh-0px)]" style={{ height: "100vh" }}>
      {/* Header */}
      <div className="flex-shrink-0 px-8 py-5 border-b border-white/10 flex items-center justify-between">
        <div>
          <p className="text-xs text-indigo-400 font-mono" style={{ fontFamily: "'JetBrains Mono', monospace" }}>
            // powered by gpt-4o
          </p>
          <h1 className="text-xl font-bold text-white tracking-tight mt-0.5" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
            AI Chat — BehaviorBot
          </h1>
        </div>
        {messages.length > 0 && (
          <button
            data-testid="clear-chat-btn"
            onClick={clearChat}
            className="flex items-center gap-1.5 text-xs text-[#94A3B8] hover:text-red-400 transition-colors duration-200"
            style={{ fontFamily: "'Inter', sans-serif" }}
          >
            <Trash2 className="w-3.5 h-3.5" />
            Hapus riwayat
          </button>
        )}
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto px-8 py-6">
        {loading ? (
          <div className="flex items-center justify-center h-full">
            <div className="w-5 h-5 border-2 border-indigo-500/30 border-t-indigo-500 rounded-full animate-spin" />
          </div>
        ) : messages.length === 0 && !streaming ? (
          <WelcomeScreen onSuggest={sendMessage} />
        ) : (
          <div className="space-y-6 max-w-3xl">
            <AnimatePresence initial={false}>
              {messages.map((msg, i) => (
                <motion.div
                  key={msg.id || i}
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.2 }}
                  data-testid={`chat-message-${msg.role}-${i}`}
                  className={`flex gap-3 ${msg.role === "user" ? "flex-row-reverse" : ""}`}
                >
                  <div className={`w-7 h-7 rounded-lg flex-shrink-0 flex items-center justify-center ${
                    msg.role === "user" ? "bg-indigo-500/20 border border-indigo-500/30" : "bg-white/5 border border-white/10"
                  }`}>
                    {msg.role === "user"
                      ? <User className="w-3.5 h-3.5 text-indigo-400" />
                      : <Bot className="w-3.5 h-3.5 text-[#94A3B8]" />
                    }
                  </div>
                  <div className={`max-w-[75%] ${msg.role === "user" ? "items-end" : "items-start"} flex flex-col`}>
                    {msg.role === "user" ? (
                      <div className="bg-indigo-600/20 border border-indigo-500/25 rounded-2xl rounded-tr-sm px-4 py-3">
                        <p className="text-sm text-white leading-relaxed" style={{ fontFamily: "'Inter', sans-serif" }}>
                          {msg.content}
                        </p>
                      </div>
                    ) : (
                      <div className="rounded-2xl rounded-tl-sm">
                        <p className="text-sm text-[#F8FAFC] leading-relaxed whitespace-pre-wrap" style={{ fontFamily: "'IBM Plex Serif', serif" }}>
                          {msg.content}
                        </p>
                      </div>
                    )}
                    <span className="text-[10px] text-[#4a5568] mt-1" style={{ fontFamily: "'JetBrains Mono', monospace" }}>
                      {new Date(msg.created_at).toLocaleTimeString('id-ID', { hour: '2-digit', minute: '2-digit' })}
                    </span>
                  </div>
                </motion.div>
              ))}
            </AnimatePresence>

            {/* Streaming Response */}
            {streaming && (
              <motion.div
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                className="flex gap-3"
                data-testid="streaming-response"
              >
                <div className="w-7 h-7 rounded-lg flex-shrink-0 flex items-center justify-center bg-white/5 border border-white/10">
                  <Bot className="w-3.5 h-3.5 text-indigo-400" />
                </div>
                <div className="max-w-[75%]">
                  <p className="text-sm text-[#F8FAFC] leading-relaxed whitespace-pre-wrap" style={{ fontFamily: "'IBM Plex Serif', serif" }}>
                    {currentResponse}
                    <span className="inline-block w-1 h-4 bg-indigo-400 ml-0.5 animate-pulse" />
                  </p>
                </div>
              </motion.div>
            )}
            <div ref={bottomRef} />
          </div>
        )}
      </div>

      {/* Input */}
      <div className="flex-shrink-0 px-8 py-5 border-t border-white/10">
        <div className="max-w-3xl flex gap-3">
          <div className="flex-1 relative">
            <textarea
              data-testid="chat-input"
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={e => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  sendMessage();
                }
              }}
              placeholder="Tanya tentang behavioral science..."
              rows={1}
              disabled={streaming}
              className="w-full bg-[#121216] border border-white/10 rounded-xl px-4 py-3 text-sm text-white placeholder-[#4a5568] focus:outline-none focus:border-indigo-500/40 resize-none disabled:opacity-50"
              style={{ fontFamily: "'Inter', sans-serif", maxHeight: "120px" }}
            />
          </div>
          <button
            data-testid="send-message-btn"
            onClick={() => sendMessage()}
            disabled={!input.trim() || streaming}
            className="flex-shrink-0 w-11 h-11 bg-indigo-600 hover:bg-indigo-500 disabled:opacity-40 disabled:cursor-not-allowed rounded-xl flex items-center justify-center transition-colors duration-200"
          >
            {streaming ? (
              <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
            ) : (
              <Send className="w-4 h-4 text-white" />
            )}
          </button>
        </div>
        <p className="text-[10px] text-[#4a5568] mt-2" style={{ fontFamily: "'JetBrains Mono', monospace" }}>
          Enter untuk kirim · Shift+Enter untuk baris baru
        </p>
      </div>
    </div>
  );
}

function WelcomeScreen({ onSuggest }) {
  return (
    <div className="max-w-3xl">
      <div className="mb-8">
        <div className="w-12 h-12 rounded-2xl bg-indigo-500/10 border border-indigo-500/20 flex items-center justify-center mb-4">
          <MessageSquare className="w-6 h-6 text-indigo-400" />
        </div>
        <h2 className="text-xl font-bold text-white mb-2" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}>
          BehaviorBot siap membantu
        </h2>
        <p className="text-sm text-[#94A3B8] leading-relaxed" style={{ fontFamily: "'Inter', sans-serif" }}>
          Tanya apapun tentang behavioral science, psikologi perilaku, atau aplikasinya dalam dunia tech dan konten kreasi.
        </p>
      </div>

      <div className="grid grid-cols-2 gap-3">
        {SUGGESTIONS.map((s, i) => (
          <button
            key={i}
            data-testid={`suggestion-${i}`}
            onClick={() => onSuggest(s)}
            className="text-left text-sm text-[#94A3B8] hover:text-white bg-[#121216] hover:bg-[#1A1A20] border border-white/10 hover:border-white/20 rounded-xl p-4 transition-colors duration-200 leading-relaxed"
            style={{ fontFamily: "'Inter', sans-serif" }}
          >
            {s}
          </button>
        ))}
      </div>
    </div>
  );
}
